import { useEffect, useState } from 'react';

import { match } from 'ts-pattern';

import { OnContactStatuses, TUser } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import useTrigger from 'lib/common/hooks/useTrigger';

import { PRESENCE_STATES } from 'lib/common/constants/presenceStates';

import connectAction from 'lib/common/utils/connectAction';
import connectGetter from 'lib/common/utils/connectGetter';

import { LogEvents, logger } from '../../components/LoggerController';
import { useAgentContext } from '../AgentContext';
import { useContactContext } from '../ContactContext';
import Context from './Context';
import debouncedStoreAgentStatus from './api/storeAgentStatus';
import useUnload from './hooks/useUnload';
import { getAgentPresence } from './utils/getAgentPresence';
import getMappedConnectState from './utils/getMappedConnectState';

const REMOTE_UPDATE_INTERVAL_S = 120;

function PresenceProvider({ children }) {
  const { agent } = useAgentContext();
  const {
    state: { tasks, onActiveCall },
  } = useContactContext();

  const onActiveTask = Boolean(tasks.length);

  const [availabilityConnectState, setAvailabilityConnectState] = useState<PRESENCE_STATES>(PRESENCE_STATES.OFFLINE); // Used for directory
  const [connectState, setConnectState] = useState<string | null>(null);

  // Consolidate connect statuses to one of AVAILABLE, BUSY, OFFLINE and CUSTOM_STATES
  const agentPresence = getAgentPresence(connectState);

  const [agentNextState, setAgentNextState] = useState<connect.AgentState | undefined>(
    connectGetter(agent, 'getNextState')
  );

  const [storeStatusTrigger, updateStoreStatusTrigger] = useTrigger({ initialValueNull: true });

  const { fetch_ } = useAuthContext();
  const { config } = useConfigContext();

  const isBusyFromConnect = agentPresence === PRESENCE_STATES.BUSY && onActiveTask;
  const onContact: TUser['onContact'] = match({
    onActiveCall,
    onActiveTask,
  })
    .with({ onActiveCall: true }, () => OnContactStatuses.OnCall)
    .with({ onActiveTask: true }, () => OnContactStatuses.OnContact)
    .otherwise(() => null);

  // Trigger store status when onContact status or availabilityConnectState changes
  useEffect(() => {
    updateStoreStatusTrigger();
  }, [onContact, availabilityConnectState]);

  const onConnectPresenceChange = ({ agent, newState }: { agent: connect.Agent; newState: string }) => {
    setAvailabilityConnectState(
      (connectGetter(agent, 'getAvailabilityState')?.state as PRESENCE_STATES) || PRESENCE_STATES.OFFLINE
    );
    // An unadulterated version of connect state, used in AgentStatusTimer
    setConnectState(newState);

    if (!onActiveTask || newState === PRESENCE_STATES.AVAILABLE) {
      setAgentNextState(undefined);
    }

    if (!newState || newState === agentPresence) {
      return;
    }

    logger.info(LogEvents.AGENT_STATUS_CHANGED.BY_CONNECT, { newState }); // agentPresence always returns undefined when called in this method

    setAgentNextState(connectGetter(agent, 'getNextState') || undefined);
  };

  const setConnectPresence = async (presence) => {
    if (!agent) {
      return;
    }

    const agentStates = connectGetter(agent, 'getAgentStates');
    const agentState = agentStates?.find((state) => state?.name === presence);

    if (!agentState) {
      return;
    }

    try {
      // This sends the request and returns success if the request is done. Then the server handles it async.
      await connectAction('setState', agent, agentState, {
        optionalPayloads: [{ enqueueNextState: true }],
        ignoreError: () => agent.getState().name === agentState.name,
      });

      const currentState = getMappedConnectState(agentState.name as PRESENCE_STATES);

      if (currentState === PRESENCE_STATES.AVAILABLE) {
        setAgentNextState(undefined);
      }

      if (currentState === agentPresence) {
        return;
      }

      logger.info(LogEvents.AGENT_STATUS_CHANGED.MANUALLY.SUCCESS, { oldState: agentPresence, newState: currentState });
    } catch (error) {
      logger.error(LogEvents.AGENT_STATUS_CHANGED.MANUALLY.FAIL, { error });
    }
  };

  useUnload({ fetch_, config });

  // The interval will update the value of storeStatusTrigger, which will cause this effect to run and store our agent status
  useEffect(() => {
    if (!storeStatusTrigger) {
      return;
    }

    // If agent is on a contact, we want to bill them as busy always
    const billingStatus = onContact ? PRESENCE_STATES.BUSY : agentPresence;

    debouncedStoreAgentStatus({
      directoryStatus: availabilityConnectState,
      onContact,
      billingStatus,
      fetch_,
      config,
    });
  }, [storeStatusTrigger]);

  useEffect(() => {
    if (!agent) {
      return;
    }

    // Set initial statuses
    const agentStatus = connectGetter(agent, 'getState')?.name;

    if (!agentStatus) {
      return;
    }

    setConnectState(agentStatus);

    const availabilityState =
      (connectGetter(agent, 'getAvailabilityState')?.state as PRESENCE_STATES) || PRESENCE_STATES.OFFLINE;

    setAvailabilityConnectState(availabilityState);

    agent.onStateChange(onConnectPresenceChange);
    agent.onEnqueuedNextState(() => setAgentNextState(connectGetter(agent, 'getNextState')));
  }, [agent]);

  useEffect(() => {
    const statusInterval = setInterval(() => {
      updateStoreStatusTrigger();
    }, REMOTE_UPDATE_INTERVAL_S * 1000);

    return () => clearInterval(statusInterval);
  }, []);

  return (
    <Context.Provider
      value={{
        setConnectPresence,
        connectState,
        visibleAgentPresence: availabilityConnectState,
        isBusyFromConnect,
        agentStates: connectGetter(agent, 'getAgentStates') || [],
        agentNextState,
        onContact,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export default PresenceProvider;
